/* ------------------------------------------------------------------------------
 *
 *  # Template JS Core - Modular Version
 *
 *  Main coordination file for all template functionality using ES6 modules
 *
 * ---------------------------------------------------------------------------- */

// Core imports
import {
	detectOS,
	transitionsEnabled,
	transitionsDisabled,
	isScrolled,
	isUpdatedDevice,
	isTouchEnabled,
	DEFAULT_THEME,
	isJQuery,
	setJQuery,
	showModal,
	hideModal
} from './core/utils.js';

import {
	isDeviceMode,
	isTablet,
	setBiggerSize,
	getPreferenceMode,
	updateBodyClass,
	renderShowCase
} from './core/device-detector.js';

import { loadResources } from './core/resource-loader.js';

// Component imports
import {
	sidebarMainResize,
	sidebarMainToggle,
	sidebarSecondaryToggle,
	sidebarRightToggle,
	sidebarComponentToggle
} from './components/sidebar.js';

import { navigationSidebar } from './components/navigation.js';

import {
	cardActionReload,
	cardActionCollapse,
	cardActionRemove,
	cardActionFullscreen
} from './components/cards.js';

// User components import
import {
	initUserGrid,
	refreshUserGrid,
	setUserCallbacks,
	viewUser,
	editUser,
	deleteUser,
	toggleUserStatus,
	applyUserFilters,
	clearUserFilters
} from './components/users.js';

// Lead components import
import {
	initLeadGrid,
	refreshLeadGrid,
	setLeadCallbacks,
	viewLead,
	editLead,
	deleteLead,
	programarVisita,
	applyLeadFilters,
	clearLeadFilters
} from './components/leads.js';

// Student components import
import {
	initStudentGrid,
	refreshStudentGrid,
	setStudentCallbacks,
	editStudent,
	toggleStudentStatus,
	applyStudentFilters,
	clearStudentFilters,
	initBirthdayPicker,
	changeAge
} from './components/students.js';

// Student activities import
import {
	initStudentActivitiesGrid,
	refreshStudentActivitiesGrid,
	setStudentActivitiesCallbacks,
	updateActivityStatus,
	viewDetails as viewActivityDetails,
	applyActivityFilters,
	clearActivityFilters
} from './components/student-activities.js';

// Mensajería import
import {
	scrollToBottom,
	updateUnreadCount,
	previewImage,
	uploadImage,
	initCameraCapture,
	handleTypingIndicator,
	formatDateSeparator
} from './components/mensajeria.js';

// Seguimiento actividades import (NUEVO)
import {
	setSeguimientoActividadesCallbacks,
	initWeekPicker,
	updateEstado,
	showSavedIndicator,
	showErrorIndicator,
	toggleCategoria,
	expandAllCategorias,
	collapseAllCategorias,
	applySearch,
	clearSearch,
	getCurrentWeek,
	exportProgressReport
} from './components/seguimiento-actividades.js';

// Student resumen import (NUEVO)
import {
	setResumenCallbacks,
	applyResumenFilters,
	clearResumenFilters,
	populateAmbientes
} from './components/student-resumen.js';

// Calendario eventos import (NUEVO)
import {
	initCalendario,
	addEventToCalendar,
	updateEventInCalendar,
	removeEventFromCalendar,
	refreshCalendarEvents,
	gotoDate,
	changeView,
	getCurrentViewInfo,
	destroyCalendar,
	setCalendarioEventosCallbacks,
	openCreateEventModal,
	openEditEventModal,
	openEventDetailsModal,
	applyCalendarioFilters,
	clearCalendarioFilters,
	initEventoDateTimePickers,
	initColorPicker
} from './components/calendario-eventos.js';

// Familias import (NUEVO)
import {
	initFamiliasGrid,
	refreshFamiliasGrid,
	setFamiliasCallbacks,
	viewFamilia,
	editFamilia,
	deleteFamilia,
	applyFilters as applyFamiliasFilters,
	clearFilters as clearFamiliasFilters
} from './components/student-familias.js';

// Student observations import
import {
	initStudentObservationsGrid,
	refreshStudentObservationsGrid,
	setStudentObservationsCallbacks,
	edit as editObservation,
	toggleVisibility as toggleObservationVisibility,
	deleteObservation,
	applyObservationFilters,
	clearObservationFilters,
	initCKEditor,
	getCKEditorContent,
	setCKEditorContent,
	destroyCKEditor,
	initDateRangePicker,
	getCurrentDateRange,
	initObservacionDateRangePicker
} from './components/student-observations.js';

// Student works import
import {
	initGaleria,
	setCallbacks as setGaleriaCallbacks,
	openModalEstudiantes,
	openModalTrabajo,
	openModalAmbientes,
	getDataAsignacionEstudiantes,
	getDataAsignacionTrabajo,
	getDataAsignacionAmbientes,
	getFotosSeleccionadas,
	getFormDataEstudiantes,
	getFormDataAmbientes,
	initTokenfieldModal,
	eliminarFoto,
	viewImages as viewWorkImages,
	initLightbox,
	initStudentWorksTable,
	refreshStudentWorksTable,
	refreshStudentWorksGallery,
	applyWorkFilters,
	clearWorkFilters
} from './components/student-works.js';

// Student profile import
import {
	initStudentProfileTabs,
	switchTab as switchStudentTab,
	loadTabContent as loadStudentTabContent,
	setStudentProfileCallbacks,
	navigateToTab,
	getCurrentTab,
	refreshTabContent,
	updateTabVisibility,
	initStudentCoverUpload,
	initDailySummaryForms
} from './components/student-profile.js';

// Calendario Leads import
import {
	initCalendarioLeads,
	addVisitaToCalendar,
	updateVisitaInCalendar,
	removeVisitaFromCalendar,
	refreshCalendarLeadsVisitas,
	gotoDateLeads,
	changeViewLeads,
	getCurrentLeadsViewInfo,
	destroyCalendarioLeads,
	setCalendarioLeadsCallbacks,
	applyCalendarioLeadsFilters,
	clearCalendarioLeadsFilters
} from './components/calendario-leads.js';

// UI imports
import {
	componentTooltip,
	componentPopover,
	componentToTopButton,
	dropdownSubmenu
} from './ui/components.js';

// Integration imports
import {
	componentDatepicker,
	componentSelect2,
	componentFileUpload,
	ajaxLoading,
	initComponentFancytree
} from './integrations/forms.js';

// Firebase Push Notifications import
import {
	initializePushNotifications,
	isNotificationSupported,
	getNotificationPermission
} from './integrations/firebase-notifications.js';

import {
	initDataTableDefaults,
	initGenericDataTable,
	refreshDataTable,
	destroyDataTable
} from './integrations/datatables.js';

import { 
	initTipoCategoriaTable, 
	refreshTipoCategoriaTable, 
	setTipoCategoriaCallbacks, 
	editTipoCategoria, 
	deleteTipoCategoria 
} from './components/tipo-categoria.js';
import { 
	initCategoriaTable, 
	refreshCategoriaTable, 
	setCategoriaCallbacks, 
	editCategoria, 
	deleteCategoria 
} from './components/categoria.js';
import { 
	initSubCategoriaTable, 
	refreshSubCategoriaTable, 
	setSubCategoriaCallbacks, 
	editSubCategoria, 
	deleteSubCategoria 
} from './components/sub-categoria.js';
import { 
	initActividadesTable, 
	refreshActividadesTable, 
	setActividadesCallbacks, 
	editActividad, 
	deleteActividad,
	applyActividadesFilters,
	clearActividadesFilters,
	initActividadesFilters
} from './components/actividades-gestion.js';

// Utility imports
import {
	highlightAll,
	wireSearchEvents,
	uploadFilesClick,
	loadFile,
	saveFile,
	inputFocus,
	updateNavLinks,
	clearElement,
	showElement,
	showMenssage,
	managementSelect,
	readOnly,
	toggleSpinner,
	toggleAjax,
	updateProgressText
} from './utils/helpers.js';

// Chart imports
import { initEnrollmentChart, initC3BarsPies } from './charts/enrollment-chart.js';

// Initialize Sweet Alert (maintain compatibility)
const swalInit = swal.mixin({
	buttonsStyling: false,
	customClass: {
		confirmButton: 'btn btn-primary',
		cancelButton: 'btn btn-light',
		denyButton: 'btn btn-light',
		input: 'form-control'
	}
});

// Initialize Noty defaults
Noty.overrideDefaults({
	theme: 'limitless',
	layout: 'topRight',
	type: 'alert',
	timeout: 2500
});

// Create global variables object for compatibility
window.appGlobals = {
	isScrolled,
	isUpdatedDevice,
	isTouchEnabled,
	isJQuery: false
};

// Setup window.sfBlazorSB object (mantener compatibilidad total)
window.sfBlazorSB = {
	dotnetRef: null,

	// Device functions
	isDevice: function (dotnetObj) {
		if (dotnetObj) {
			this.dotnetRef = dotnetObj;
		}
		return isDeviceMode();
	},
	isDeviceMode,
	isTablet,
	isJQuery: () => window.appGlobals.isJQuery,

	// Core functions
	inputFocus,
	updateBodyClass,
	renderShowCase,
	setBiggerSize,
	getPreferenceMode,
	loadResources,
	wireSearchEvents,
	uploadFilesClick,
	loadFile,
	saveFile,

	// DataTable functions
	dataTables: {
		init: initGenericDataTable,
		refresh: refreshDataTable,
		destroy: destroyDataTable,
		initDefaults: initDataTableDefaults
	},

	select2: {
		init: function (selectId, data, dotNetRef, methodInvoke,selectedValues, options = {}) {
			if (!$().select2) {
				console.warn('Warning - select2.min.js is not loaded.');
				return;
			}
			
			const $select = $(`#${selectId}`);
			
			// Destruir instancia previa si existe
			if ($select.hasClass('select2-hidden-accessible')) {
				$select.select2('destroy');
				$select.empty();
			}
			
			// Desvincular eventos previos para evitar duplicados
			$select.off('select2:select select2:unselect change');
			
			const defaultOptions = {
				allowClear: true,
				...options  // Sobrescribe con las opciones pasadas desde Blazor
			};
			
			$select.select2({ ...defaultOptions, data: data });

			// Configura el evento 'change' para invocar la función de Blazor
			if (dotNetRef) {
				$select.on('select2:select', function () {
					const selected = $(this).val() || [];
					dotNetRef.invokeMethodAsync(methodInvoke, selected);
				});
				
				$select.on('select2:unselecting', function () {
					const selected = $(this).val() || '';
					dotNetRef.invokeMethodAsync(methodInvoke, selected);
				});
			}
			if (selectedValues != null) {
				$select.val(selectedValues).trigger('change');
			}
		},
		destroy: function (selectId) {
			const $select = $(`#${selectId}`);
			if ($select.length && $select.hasClass('select2-hidden-accessible')) {
				// Desvincular eventos antes de destruir
				$select.off('select2:select select2:unselect change');
				$select.select2('destroy');
			}
		}
	},

	// User-specific functions
	users: {
		initUserGrid,
		refreshUserGrid,
		setCallbacks: setUserCallbacks,
		viewUser,
		editUser,
		deleteUser,
		toggleStatus: toggleUserStatus,
		applyFilters: applyUserFilters,
		clearFilters: clearUserFilters
	},

	// Lead-specific functions
	leads: {
		initLeadGrid,
		refreshLeadGrid,
		setCallbacks: setLeadCallbacks,
		viewLead,
		editLead,
		deleteLead,
		programarVisita,
		applyFilters: applyLeadFilters,
		clearFilters: clearLeadFilters,
		
		// Calendario de visitas
		calendario: {
			init: initCalendarioLeads,
			addVisita: addVisitaToCalendar,
			updateVisita: updateVisitaInCalendar,
			removeVisita: removeVisitaFromCalendar,
			refreshVisitas: refreshCalendarLeadsVisitas,
			gotoDate: gotoDateLeads,
			changeView: changeViewLeads,
			getCurrentViewInfo: getCurrentLeadsViewInfo,
			destroy: destroyCalendarioLeads,
			setCallbacks: setCalendarioLeadsCallbacks,
			applyFilters: applyCalendarioLeadsFilters,
			clearFilters: clearCalendarioLeadsFilters
		}
	},

	// Student-specific functions
	students: {
		initStudentGrid,
		refreshStudentGrid,
		setCallbacks: setStudentCallbacks,
		editStudent,
		toggleStudentStatus,
		applyFilters: applyStudentFilters,
		clearFilters: clearStudentFilters,
		initBirthdayPicker,
		changeAge,
		
		// Activities module
		activities: {
			initGrid: initStudentActivitiesGrid,
			refresh: refreshStudentActivitiesGrid,
			setCallbacks: setStudentActivitiesCallbacks,
			updateStatus: updateActivityStatus,
			viewDetails: viewActivityDetails,
			applyFilters: applyActivityFilters,
			clearFilters: clearActivityFilters
		},
		
		// Seguimiento module (NUEVO)
		seguimiento: {
			setCallbacks: setSeguimientoActividadesCallbacks,
			initWeekPicker: initWeekPicker,
			updateEstado: updateEstado,
			showSavedIndicator: showSavedIndicator,
			showErrorIndicator: showErrorIndicator,
			toggleCategoria: toggleCategoria,
			expandAll: expandAllCategorias,
			collapseAll: collapseAllCategorias,
			applySearch: applySearch,
			clearSearch: clearSearch,
			getCurrentWeek: getCurrentWeek,
			applyFilters: function() { 
				const searchName = document.getElementById('searchName')?.value || '';
				const selectedAmbienteId = document.getElementById('ambienteFilter')?.value || '';
				const weekValue = document.getElementById('weekFilterActividades')?.value || '';
				window.applyFiltersCallback?.invokeMethodAsync('ApplyFilters', searchName, selectedAmbienteId, weekValue); 
			},
			clearFilters: function() { 
				const today = new Date();
				const year = today.getFullYear();
				const week = getWeekNumber(today);
				document.getElementById('weekFilterActividades').value = `${year}-W${week.toString().padStart(2, '0')}`;
				document.getElementById('searchName').value = '';
				document.getElementById('ambienteFilter').value = '';
				window.applyFiltersCallback?.invokeMethodAsync('ClearFilters'); 
			},
			
			// Resumen module
			resumen: {
				setCallbacks: setResumenCallbacks,
				applyFilters: applyResumenFilters,
				clearFilters: clearResumenFilters,
				populateAmbientes: populateAmbientes
			}
		},
		
		// Observations module
		observations: {
			initGrid: initStudentObservationsGrid,
			refresh: refreshStudentObservationsGrid,
			setCallbacks: setStudentObservationsCallbacks,
			edit: editObservation,
			toggleVisibility: toggleObservationVisibility,
			deleteObservation: deleteObservation,
			applyFilters: applyObservationFilters,
			clearFilters: clearObservationFilters,
			// CKEditor functions
			initCKEditor: initCKEditor,
			getCKEditorContent: getCKEditorContent,
			setCKEditorContent: setCKEditorContent,
			destroyCKEditor: destroyCKEditor,
			// DateRangePicker functions
			initDateRangePicker: initDateRangePicker,
			getCurrentDateRange: getCurrentDateRange,
			initObservacionDateRangePicker: initObservacionDateRangePicker
		},
		
		// Works/Gallery module
		works: {
			initGaleria: initGaleria,
			setCallbacks: setGaleriaCallbacks,
			openModalEstudiantes: openModalEstudiantes,
			openModalTrabajo: openModalTrabajo,
			openModalAmbientes: openModalAmbientes,
			getDataAsignacionEstudiantes: getDataAsignacionEstudiantes,
			getDataAsignacionTrabajo: getDataAsignacionTrabajo,
			getDataAsignacionAmbientes: getDataAsignacionAmbientes,
			getFotosSeleccionadas: getFotosSeleccionadas,
			getFormDataEstudiantes: getFormDataEstudiantes,
			getFormDataAmbientes: getFormDataAmbientes,
			initTokenfieldModal: initTokenfieldModal,
			eliminarFoto: eliminarFoto,
			initLightbox: initLightbox,
			initTable: initStudentWorksTable,
			refreshTable: refreshStudentWorksTable,
			refreshGallery: refreshStudentWorksGallery,
			applyFilters: applyWorkFilters,
			clearFilters: clearWorkFilters,
			viewImages: viewWorkImages,
		},
		
		// Profile module
		profile: {
			initTabs: initStudentProfileTabs,
			switchTab: switchStudentTab,
			loadTabContent: loadStudentTabContent,
			setCallbacks: setStudentProfileCallbacks,
			navigateToTab: navigateToTab,
			getCurrentTab: getCurrentTab,
			refreshTabContent: refreshTabContent,
			updateTabVisibility: updateTabVisibility,
			initCoverUpload: initStudentCoverUpload,
			initDailySummaryForms: initDailySummaryForms,
		}
	},

	// Familias module (NUEVO)
	familias: {
		initGrid: initFamiliasGrid,
		refresh: refreshFamiliasGrid,
		setCallbacks: setFamiliasCallbacks,
		viewFamilia: viewFamilia,
		editFamilia: editFamilia,
		deleteFamilia: deleteFamilia,
		applyFilters: applyFamiliasFilters,
		clearFilters: clearFamiliasFilters
	},

	// Calendario Eventos module (NUEVO)
	calendarioEventos: {
		init: initCalendario,
		addEvent: addEventToCalendar,
		updateEvent: updateEventInCalendar,
		removeEvent: removeEventFromCalendar,
		refreshEvents: refreshCalendarEvents,
		gotoDate: gotoDate,
		changeView: changeView,
		getCurrentViewInfo: getCurrentViewInfo,
		destroy: destroyCalendar,
		setCallbacks: setCalendarioEventosCallbacks,
		openCreateModal: openCreateEventModal,
		openEditModal: openEditEventModal,
		openDetailsModal: openEventDetailsModal,
		applyFilters: applyCalendarioFilters,
		clearFilters: clearCalendarioFilters,
		initDateTimePickers: initEventoDateTimePickers,
		initColorPicker: initColorPicker
	},

	// Mensajería module (NUEVO)
	mensajeria: {
		scrollToBottom: scrollToBottom,
		updateUnreadCount: updateUnreadCount,
		previewImage: previewImage,
		uploadImage: uploadImage,
		initCameraCapture: initCameraCapture,
		handleTypingIndicator: handleTypingIndicator,
		formatDateSeparator: formatDateSeparator
	},

	// Ambientes module
	ambientes: {
		tipoCategorias: {
			init: initTipoCategoriaTable,
			refresh: refreshTipoCategoriaTable,
			setCallbacks: setTipoCategoriaCallbacks,
			edit: editTipoCategoria,
			delete: deleteTipoCategoria
		},
		categorias: {
			init: initCategoriaTable,
			refresh: refreshCategoriaTable,
			setCallbacks: setCategoriaCallbacks,
			edit: editCategoria,
			delete: deleteCategoria
		},
		subCategorias: {
			init: initSubCategoriaTable,
			refresh: refreshSubCategoriaTable,
			setCallbacks: setSubCategoriaCallbacks,
			edit: editSubCategoria,
			delete: deleteSubCategoria
		}
	},

	// Actividades module
	actividades: {
		init: initActividadesTable,
		refresh: refreshActividadesTable,
		setCallbacks: setActividadesCallbacks,
		edit: editActividad,
		delete: deleteActividad,
		initFilters: initActividadesFilters,
		applyFilters: applyActividadesFilters,
		clearFilters: clearActividadesFilters
	},

	// Legacy support (deprecated)
	tipoCategorias: {
		init: initTipoCategoriaTable,
		refresh: refreshTipoCategoriaTable,
		setCallbacks: setTipoCategoriaCallbacks,
		edit: editTipoCategoria,
		delete: deleteTipoCategoria
	},
	categorias: {
		init: initCategoriaTable,
		refresh: refreshCategoriaTable,
		setCallbacks: setCategoriaCallbacks,
		edit: editCategoria,
		delete: deleteCategoria
	},
	subCategorias: {
		init: initSubCategoriaTable,
		refresh: refreshSubCategoriaTable,
		setCallbacks: setSubCategoriaCallbacks,
		edit: editSubCategoria,
		delete: deleteSubCategoria
	},

	// Firebase Push Notifications (NUEVO)
	pushNotifications: {
		initialize: initializePushNotifications,
		isSupported: isNotificationSupported,
		getPermission: getNotificationPermission
	},

	utils: {
		showModal: showModal,
		hideModal: hideModal
	},
	// Notification function using SweetAlert
	showNotification: function (message, icon, title, isHtml, redirectUrl) {
		const callId = Math.random().toString(36).substring(7);
		const validIcons = ["success", "error", "warning", "info", "question"];
		if (!validIcons.includes(icon)) {
			icon = "success";
		}

		const options = {
			icon: icon,
		};

		if (title) {
			options.title = title;
		}

		if (isHtml) {
			options.html = message;
		} else {
			options.text = message;
		}

		swalInit.fire(options).then(function (result) {
			console.log(`[${callId}] EN THEN con redirectUrl:`, redirectUrl);
			if (result.isConfirmed && redirectUrl) {
				window.location.href = redirectUrl;
			}
		});
	},
	notification: function (message, type) {
		new Noty({ text: message, type: type }).show()
	},

	// Chart functions
	initEnrollmentChart: function (elementId, series, startYear) {
		initEnrollmentChart();
		if (window.sfBlazorSB.initEnrollmentChart) {
			return window.sfBlazorSB.initEnrollmentChart(elementId, series, startYear);
		}
	},

	// Main app object
	app: function () {
		return {
			// Core initialization functions
			initBeforeLoad: function () {
				detectOS();
			},

			initAfterLoad: function () {
				transitionsEnabled();
			},

			// Component initialization
			initComponents: function () {
				componentTooltip();
				componentPopover();
				componentToTopButton();
			},

			// Sidebar initialization
			initSidebars: function () {
				sidebarMainResize();
				sidebarMainToggle();
				sidebarSecondaryToggle();
				sidebarRightToggle();
				sidebarComponentToggle();
			},

			// Navigation initialization
			initNavigations: function () {
				navigationSidebar();
			},

			// Card actions initialization
			initCardActions: function () {
				cardActionReload();
				cardActionCollapse();
				cardActionRemove();
				cardActionFullscreen();
			},

			// Dropdown initialization
			initDropdowns: function () {
				dropdownSubmenu();
			},

			// Highlight initialization
			initHighlightAll: function () {
				highlightAll();
			},

			// Form component initialization
			initPicker: function () {
				componentDatepicker();
			},

			initSelect2: function () {
				componentSelect2();
			},

			initComponentFileUpload: function () {
				componentFileUpload();
			},

			initComponentAjaxLoading: function () {
				ajaxLoading();
			},

			initComponentFancytree: function () {
				initComponentFancytree();
			},

			// DataTables initialization
			initDataTables: function () {
				initDataTableDefaults();
			},
			// Chart initialization
			initC3BarsPies: function (id, data) {
				initC3BarsPies(id, data);
			},

			// Core initialization function
			initCore: function () {
				this.initBeforeLoad();
				this.initSidebars();
				this.initNavigations();
				this.initComponents();
				this.initCardActions();
				this.initComponentAjaxLoading();
				this.initDataTables();
				this.initHighlightAll();
			},

			// UI helper functions
			updateNavLinks,
			clearElement,
			showElement,
			showMenssage,
			managementSelect,
			readOnly
		};
	}(),

	// Spinner functions
	toggleSpinner,
	toggleAjax,
	updateProgressText
};

// Assign swalInit to window for global availability (maintain compatibility)
window.swalInit = swalInit;

// Initialize chart functionality
initEnrollmentChart();

// Set jQuery detection
if (typeof jQuery !== 'undefined') {
	window.appGlobals.isJQuery = true;
	setJQuery(true);
}

// Auto-initialize core functionality when DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', function () {
		window.sfBlazorSB.app.initCore();
	});
} else {
	// DOM is already ready
	window.sfBlazorSB.app.initCore();
}

// Assign showNotification to window for direct access
window.showNotification = window.sfBlazorSB.showNotification;

// Assign showNotification to window for direct access
window.notification = window.sfBlazorSB.notification;

// Set callback for filters
window.setApplyFiltersCallback = function(dotnetRef) {
	window.applyFiltersCallback = dotnetRef;
};

// Function to get week number
function getWeekNumber(date) {
	const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
	const dayNum = d.getUTCDay() || 7;
	d.setUTCDate(d.getUTCDate() + 4 - dayNum);
	const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
	return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// ============================================================================
// Global Functions for Observaciones Module (Compatibility Layer)
// ============================================================================

// Callback reference para Observaciones
window.observacionesFiltersCallback = null;

// Set callback from Blazor
window.setObservacionesFiltersCallback = function(dotnetRef) {
	window.observacionesFiltersCallback = dotnetRef;
};

// Apply Observaciones Filters
window.applyObservacionesFilters = function() {
	const categoria = document.getElementById('categoriaFilterObservaciones')?.value || '';
	const periodo = document.getElementById('periodoFilterObservaciones')?.value || '90';
	
	if (window.observacionesFiltersCallback) {
		window.observacionesFiltersCallback.invokeMethodAsync('ApplyFilters', categoria, periodo);
	}
};

// Clear Observaciones Filters
window.clearObservacionesFilters = function() {
	const categoriaSelect = document.getElementById('categoriaFilterObservaciones');
	const periodoSelect = document.getElementById('periodoFilterObservaciones');
	
	if (categoriaSelect) categoriaSelect.value = '';
	if (periodoSelect) periodoSelect.value = '90';
	
	if (window.observacionesFiltersCallback) {
		window.observacionesFiltersCallback.invokeMethodAsync('ClearFilters');
	}
};

// Initialize DateRangePicker for Observacion Modal (Global wrapper)
window.initObservacionDateRangePicker = function(inputId, dotNetRef) {
	return window.sfBlazorSB.students.observations.initObservacionDateRangePicker(inputId, dotNetRef);
};

// ============================================================================
// Global Functions for File Downloads
// ============================================================================

/**
 * Download file from Base64 string
 * @param {string} base64String - Base64 encoded file content
 * @param {string} fileName - Name for the downloaded file
 */
window.downloadFileFromBase64 = function(base64String, fileName) {
	try {
		// Convert base64 to byte array
		const byteCharacters = atob(base64String);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		
		// Create blob and download
		const blob = new Blob([byteArray], { type: 'application/pdf' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	} catch (error) {
		console.error('Error downloading file:', error);
		if (window.showNotification) {
			window.showNotification('Error al descargar el archivo', 'error');
		}
	}
};
